// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.D3LineChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import * as d3 from 'd3';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { first, size, get, isUndefined, sumBy, isEmpty, uniqueId, escape, isNull, curry } from 'lodash/fp';
import TreemapChartOptionsBuilder from 'modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.TreemapChartOptionsBuilder';
import { DEFAULT_COLOR_PALETTE } from 'logic/data/Izenda.UIConstants';
import { getTooltipFormatterForPoint } from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import { DISABLE_LEGEND_COLOR } from 'logic/data/Izenda.D3VizEngineConstants';

@exportClass('D3TreeMapChartOptionsBuilder')
export default class D3TreeMapChartOptionsBuilder extends TreemapChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  build() {
    const options = super.build();
    const d3TreeMapOptions = this.buildD3TreeMapOptions(options);
    return { ...d3TreeMapOptions, type: this.visualType };
  }

  /**
   * Build D3 Tree Map options
   * @param {Object} options
   */
  buildD3TreeMapOptions(options: Object) {
    this.convertToTreeNode(options);
    this.addTooltipFormatter(options);
    this.setColor(options);
    return options;
  }

  /**
   * Convert serie data to D3 Tree Node
   * @param {Object} options
   */
  convertToTreeNode(options: Object) {
    const { series, colors = DEFAULT_COLOR_PALETTE } = options;
    const izMultiColor = get('plotOptions.series.colorByPoint', options);
    if (!isEmpty(series)) {
      const { data } = first(series);
      const rootNodeId = `TreeMapRoot_${uniqueId()};#`;

      if (!isEmpty(data)) {
        // Add root node as D3 do not allow multiple roots
        const rootObject = {
          name: '',
          id: rootNodeId,
          parent: '',
          isRoot: true
        };

        data.forEach((node, index) => {
          // Set node color
          node.color = node.color || colors[izMultiColor ? index % size(colors) : 0];
          isUndefined(node.parent) && (node.parent = rootNodeId);
        });

        // We use d3.each to set value instead of d3.sum because the records already has value property
        // as sum of each child nodes
        // Using d3.sum will add that value again and double the correct sum
        const root = d3
          .stratify()
          .id(d => d.id)
          .parentId(d => d.parent)([rootObject, ...data])
          .each(node => (node.value = node.data.value))
          .sort((a, b) => b.value - a.value);

        // Calculate the sum for the root
        root.value = sumBy('value', root.children);
        options.series[0].data = root;
      }
    }
  }

  /**
   * Add tooltip formatter to options
   * @param {Object} options
   */
  addTooltipFormatter(options: Object) {
    options.tooltip && (options.tooltip.formatter = curry(buildTooltipContent)(options));
  }

  /**
   * Set color for the series
   * @param {Object} options 
   */
  setColor(options: Object) {
    const { series, colors = DEFAULT_COLOR_PALETTE } = options;
    const izMultiColor = get('plotOptions.series.colorByPoint', options);
    first(series).color = izMultiColor ? DISABLE_LEGEND_COLOR : (first(series).color || first(colors));
  }
}

/**
 * Build tooltip content
 * @param {*} options
 * @param {*} point
 * @param {*} serie
 */
function buildTooltipContent(options, point, serie) {
  const tooltipPointParam = {
    ...point,
    series: {
      userOptions: serie,
      chart: { userOptions: options }
    },
    point: { options: point }
  };
  return getTooltipFormatterForPoint([tooltipPointParam]);
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/treemap/Izenda.D3TreeMapChartOptionsBuilder.js